/**
 * /ai/improvement — the self-improving flywheel console.
 *   • Corrections ledger with the error-taxonomy histogram
 *   • Run tune (LLM proposes prompt; adoption gated by golden eval)
 *   • Fine-tune panel: progress toward the export gate, export trigger
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  Box,
  Heading,
  HStack,
  VStack,
  Text,
  Button,
  Select,
  Spinner,
  Center,
  Tag,
  Progress,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Alert,
  AlertIcon,
  Checkbox,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiRefreshCw, FiZap, FiUploadCloud } from 'react-icons/fi';
import { callDocai, useRunPoller } from '../../hooks/useDocAI';
import RunProgressBar from '../../components/AI/RunProgressBar';

const FINETUNE_GATE = 150; // verified docs needed before export unlocks (plan gate)

export default function AIImprovementPage() {
  const [skills, setSkills] = useState([]);
  const [skillName, setSkillName] = useState('uwm-lock-confirmation');
  const [stats, setStats] = useState(null);
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeSilver, setIncludeSilver] = useState(true);
  const [tuneRunId, setTuneRunId] = useState(null);
  const [exportRunId, setExportRunId] = useState(null);
  const { run: tuneRun } = useRunPoller(tuneRunId);
  const { run: exportRun } = useRunPoller(exportRunId);
  const toast = useToast();
  const history = useHistory();
  const { url } = useRouteMatch();
  const sectionUrl = url.replace(/\/improvement$/, '');

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    callDocai('docaiSkills', { action: 'list' })
      .then((res) => setSkills((res.skills || []).filter((s) => !s.name.startsWith('_'))))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callDocai('docaiStats', { action: 'dashboard', skill: skillName });
      setStats(res.scoreboard || null);
      // Corrections detail comes from the review workspace data via documents;
      // the scoreboard already aggregates the ledger. For per-record display,
      // list open corrections through the documents of this skill is heavy —
      // the ledger summary + taxonomy histogram carries the page.
      setCorrections(res.scoreboard?.corrections || null);
    } catch (err) {
      toast({ title: 'Load failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setLoading(false);
    }
  }, [skillName, toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const done = (run, label, reset) => {
      if (run && ['done', 'error', 'cancelled'].includes(run.status)) {
        if (run.status === 'done') toast({ title: `${label} complete`, status: 'success', duration: 4000 });
        if (run.status === 'error') toast({ title: `${label} failed`, description: run.error, status: 'error', duration: 8000 });
        reset(null);
        load();
      }
    };
    done(tuneRun, 'Tune', setTuneRunId);
    done(exportRun, 'Fine-tune export', setExportRunId);
  }, [tuneRun, exportRun, toast, load]);

  const startTune = async () => {
    try {
      const res = await callDocai('docaiRuns', { action: 'start', kind: 'tune', skill: skillName, params: { rounds: 2 } });
      setTuneRunId(res.runId);
      toast({ title: 'Tune queued — proposals are eval-gated before adoption', status: 'info', duration: 4000 });
    } catch (err) {
      toast({ title: 'Tune start failed', description: err.message, status: 'error', duration: 6000 });
    }
  };

  const startExport = async (acknowledgeGates = false) => {
    try {
      const res = await callDocai('docaiRuns', {
        action: 'start',
        kind: 'export-finetune',
        skill: skillName,
        params: { includeSilver, acknowledgeGates },
      });
      setExportRunId(res.runId);
      toast({ title: 'Export queued', status: 'info', duration: 3000 });
    } catch (err) {
      toast({ title: 'Export start failed', description: err.message, status: 'error', duration: 6000 });
    }
  };

  if (loading && !stats) {
    return <Center h="60vh"><Spinner size="xl" color="blue.400" /></Center>;
  }

  const corr = corrections || {};
  const goldens = stats?.goldens ?? 0;
  const silver = stats?.silverCorpus?.records ?? 0;
  const verifiedTotal = goldens + silver;
  const gatePct = Math.min(100, Math.round((verifiedTotal / FINETUNE_GATE) * 100));
  const taxonomy = Object.entries(corr.errorTaxonomy || {}).sort((a, b) => b[1] - a[1]);
  const maxTag = taxonomy.length ? taxonomy[0][1] : 1;

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={5} flexWrap="wrap">
        <Heading size="md">Improvement</Heading>
        <HStack>
          <Select size="sm" w="260px" value={skillName} onChange={(e) => setSkillName(e.target.value)}>
            {(skills.length ? skills : [{ name: skillName }]).map((s) => (
              <option key={s.name} value={s.name}>{s.name}</option>
            ))}
          </Select>
          <Button size="sm" leftIcon={<FiRefreshCw />} variant="outline" onClick={load} isLoading={loading}>
            Refresh
          </Button>
        </HStack>
      </HStack>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={6}>
        <Stat bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" px={4} py={3}>
          <StatLabel fontSize="xs">Correction records</StatLabel>
          <StatNumber>{corr.records ?? 0}</StatNumber>
          <StatHelpText fontSize="xs" mb={0}>{corr.open ?? 0} open · {corr.resolved ?? 0} resolved</StatHelpText>
        </Stat>
        <Stat bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" px={4} py={3}>
          <StatLabel fontSize="xs">Fields fixed by humans</StatLabel>
          <StatNumber>{corr.fieldsFixedByHumans ?? 0}</StatNumber>
          <StatHelpText fontSize="xs" mb={0}>{corr.confirmedCleanPromotions ?? 0} clean promotions</StatHelpText>
        </Stat>
        <Stat bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" px={4} py={3}>
          <StatLabel fontSize="xs">Tuning proposals</StatLabel>
          <StatNumber>{stats?.tuning?.proposals ?? 0}</StatNumber>
          <StatHelpText fontSize="xs" mb={0}>{stats?.tuning?.adopted ?? 0} adopted (eval-gated)</StatHelpText>
        </Stat>
        <Stat bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" px={4} py={3}>
          <StatLabel fontSize="xs">Verified corpus</StatLabel>
          <StatNumber>{verifiedTotal}</StatNumber>
          <StatHelpText fontSize="xs" mb={0}>{goldens} golden + {silver} silver</StatHelpText>
        </Stat>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Tune panel */}
        <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={5}>
          <Heading size="sm" mb={2}>Auto-tune the prompt</Heading>
          <Text fontSize="sm" color="gray.500" mb={4}>
            The tuner digests open corrections + golden eval diffs, proposes a revised prompt, and adopts it ONLY
            if the golden eval strictly improves with zero regressions. Rejected proposals land in the version
            history for review.
          </Text>
          {tuneRun && <Box mb={3}><RunProgressBar run={tuneRun} /></Box>}
          <HStack>
            <Button size="sm" colorScheme="blue" leftIcon={<FiZap />} onClick={startTune} isDisabled={!!tuneRunId}>
              Run tune (2 rounds)
            </Button>
            <Button size="sm" variant="ghost" onClick={() => history.push(`${sectionUrl}/runs`)}>
              Past tune runs
            </Button>
          </HStack>
          {(corr.open ?? 0) === 0 && (
            <Alert status="info" mt={3} borderRadius="md" fontSize="xs">
              <AlertIcon />
              No open corrections — the tuner works best after you promote corrected documents in Review.
            </Alert>
          )}
        </Box>

        {/* Fine-tune panel */}
        <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={5}>
          <Heading size="sm" mb={2}>Fine-tune dataset</Heading>
          <Text fontSize="sm" color="gray.500" mb={3}>
            Exports verified extractions as Vertex AI Gemini supervised-tuning JSONL. The gate keeps the dataset
            honest: {FINETUNE_GATE}+ verified documents before export unlocks.
          </Text>
          <HStack mb={1} justify="space-between">
            <Text fontSize="xs" color="gray.500">{verifiedTotal} / {FINETUNE_GATE} verified documents</Text>
            <Text fontSize="xs" fontWeight="bold">{gatePct}%</Text>
          </HStack>
          <Progress value={gatePct} size="sm" borderRadius="md" colorScheme={gatePct >= 100 ? 'green' : 'blue'} mb={4} />
          <Checkbox
            size="sm"
            isChecked={includeSilver}
            onChange={(e) => setIncludeSilver(e.target.checked)}
            mb={3}
          >
            <Text fontSize="sm">Include silver corpus (verifier + LOS-backed auto-accepts)</Text>
          </Checkbox>
          {exportRun && <Box mb={3}><RunProgressBar run={exportRun} /></Box>}
          <HStack>
            <Button
              size="sm"
              colorScheme={gatePct >= 100 ? 'green' : 'gray'}
              leftIcon={<FiUploadCloud />}
              onClick={() => startExport(false)}
              isDisabled={!!exportRunId}
            >
              Export dataset
            </Button>
            {gatePct < 100 && (
              <Button size="sm" variant="outline" onClick={() => startExport(true)} isDisabled={!!exportRunId}>
                Export anyway (acknowledge gate)
              </Button>
            )}
          </HStack>
        </Box>
      </SimpleGrid>

      {/* Error taxonomy histogram */}
      <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="lg" p={5} mt={6}>
        <Heading size="sm" mb={3}>Error taxonomy (from correction field diffs)</Heading>
        {taxonomy.length ? (
          <VStack align="stretch" spacing={2}>
            {taxonomy.map(([tag, n]) => (
              <HStack key={tag} spacing={3}>
                <Tag size="sm" w="200px" justifyContent="center">{tag}</Tag>
                <Progress value={(n / maxTag) * 100} size="sm" borderRadius="md" flex={1} />
                <Text fontSize="xs" w="30px" textAlign="right" fontWeight="bold">{n}</Text>
              </HStack>
            ))}
          </VStack>
        ) : (
          <Text fontSize="sm" color="gray.500">
            No tagged corrections yet — the histogram fills as you promote corrected documents.
          </Text>
        )}
      </Box>
    </Box>
  );
}
